import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Polyline } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const MutualFundReturnsCalculatorScreen = () => {
  const { colors } = useTheme();
  const [investmentType, setInvestmentType] = useState('sip');
  const [fundCategory, setFundCategory] = useState('large-cap');
  const [sipAmount, setSipAmount] = useState('10000');
  const [lumpSumAmount, setLumpSumAmount] = useState('500000');
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [investmentPeriod, setInvestmentPeriod] = useState(10);
  const [stepUpPercentage, setStepUpPercentage] = useState(10);
  const [expenseRatio, setExpenseRatio] = useState(1.5);
  const [exitLoad, setExitLoad] = useState(1);
  const [exitLoadPeriod, setExitLoadPeriod] = useState(1);
  const [inflationRate, setInflationRate] = useState(6);
  const [goalAmount, setGoalAmount] = useState('2000000');
  const [taxRate, setTaxRate] = useState(10);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showYearlyBreakdown, setShowYearlyBreakdown] = useState(false);
  const [showGoalPlanning, setShowGoalPlanning] = useState(false);
  const [results, setResults] = useState(null);
  const [displayedRows, setDisplayedRows] = useState(5);

  const investmentTypes = [
    { label: 'SIP', value: 'sip', icon: '📈' },
    { label: 'Lump Sum', value: 'lumpsum', icon: '💰' },
    { label: 'SIP + Lump Sum', value: 'hybrid', icon: '🔄' },
  ];

  const fundCategories = [
    { 
      label: 'Large Cap Equity', 
      value: 'large-cap', 
      expectedReturn: 12, 
      risk: 'Medium',
      expenseRatio: 1.2,
      description: 'Invests in top 100 companies by market cap'
    },
    { 
      label: 'Mid Cap Equity', 
      value: 'mid-cap', 
      expectedReturn: 14, 
      risk: 'High',
      expenseRatio: 1.8,
      description: 'Invests in companies ranked 101-250 by market cap'
    },
    { 
      label: 'Small Cap Equity', 
      value: 'small-cap', 
      expectedReturn: 16, 
      risk: 'Very High',
      expenseRatio: 2.0,
      description: 'Invests in companies ranked beyond 250 by market cap'
    },
    { 
      label: 'Multi Cap Equity', 
      value: 'multi-cap', 
      expectedReturn: 13, 
      risk: 'Medium-High',
      expenseRatio: 1.5,
      description: 'Flexible allocation across market caps'
    },
    { 
      label: 'Hybrid Funds', 
      value: 'hybrid', 
      expectedReturn: 10, 
      risk: 'Medium',
      expenseRatio: 1.3,
      description: 'Mix of equity and debt investments'
    },
    { 
      label: 'Debt Funds', 
      value: 'debt', 
      expectedReturn: 7, 
      risk: 'Low',
      expenseRatio: 0.8,
      description: 'Invests in bonds and fixed income securities'
    },
    { 
      label: 'ELSS Funds', 
      value: 'elss', 
      expectedReturn: 13, 
      risk: 'Medium-High',
      expenseRatio: 1.4,
      description: 'Tax-saving equity funds with 3-year lock-in'
    },
    { 
      label: 'Index Funds', 
      value: 'index', 
      expectedReturn: 11, 
      risk: 'Medium',
      expenseRatio: 0.3,
      description: 'Passively tracks market indices'
    },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateXIRR = (cashFlows, dates) => {
    // Simplified XIRR calculation using Newton-Raphson method
    let rate = 0.1; // Initial guess
    const tolerance = 1e-6;
    const maxIterations = 100;

    for (let i = 0; i < maxIterations; i++) {
      let npv = 0;
      let dnpv = 0;

      for (let j = 0; j < cashFlows.length; j++) {
        const daysDiff = (dates[j] - dates[0]) / (1000 * 60 * 60 * 24);
        const yearsDiff = daysDiff / 365.25;
        const factor = Math.pow(1 + rate, yearsDiff);
        
        npv += cashFlows[j] / factor;
        dnpv -= cashFlows[j] * yearsDiff / factor / (1 + rate);
      }

      if (Math.abs(npv) < tolerance) {
        return rate * 100;
      }

      rate = rate - npv / dnpv;
    }

    return rate * 100;
  };

  const calculateMutualFundReturns = () => {
    const sip = parseFloat(sipAmount) || 0;
    const lumpSum = parseFloat(lumpSumAmount) || 0;
    const returnRate = parseFloat(expectedReturn) || 0;
    const period = parseFloat(investmentPeriod) || 0;
    const stepUp = parseFloat(stepUpPercentage) || 0;
    const expense = parseFloat(expenseRatio) || 0;
    const inflation = parseFloat(inflationRate) || 0;
    const goal = parseFloat(goalAmount) || 0;
    const tax = parseFloat(taxRate) || 0;

    // Adjust return rate for expense ratio
    const netReturnRate = returnRate - expense;
    const monthlyReturn = netReturnRate / (12 * 100);
    const realReturn = ((1 + netReturnRate/100) / (1 + inflation/100) - 1) * 100;

    let totalInvestment = 0;
    let maturityValue = 0;
    let yearlyBreakdown = [];
    let cashFlows = [];
    let dates = [];

    const startDate = new Date();

    if (investmentType === 'sip' || investmentType === 'hybrid') {
      let currentSIP = sip;
      let runningTotal = 0;

      for (let year = 1; year <= period; year++) {
        let yearStartValue = runningTotal;
        let yearInvestment = 0;
        let yearEndValue = runningTotal;

        for (let month = 1; month <= 12; month++) {
          // Apply monthly return
          runningTotal = runningTotal * (1 + monthlyReturn);
          
          // Add SIP investment
          runningTotal += currentSIP;
          yearInvestment += currentSIP;
          totalInvestment += currentSIP;

          // Add to cash flows for XIRR calculation
          if (year === 1 && month === 1) {
            dates.push(new Date(startDate.getFullYear(), startDate.getMonth() + (year - 1) * 12 + month - 1, 1));
          }
          cashFlows.push(-currentSIP);
          dates.push(new Date(startDate.getFullYear(), startDate.getMonth() + (year - 1) * 12 + month - 1, 1));
        }

        yearEndValue = runningTotal;

        // Apply step-up at year end
        if (stepUp > 0) {
          currentSIP = currentSIP * (1 + stepUp / 100);
        }

        yearlyBreakdown.push({
          year,
          startValue: yearStartValue,
          investment: yearInvestment,
          endValue: yearEndValue,
          sipAmount: currentSIP / (1 + stepUp / 100), // SIP amount for this year
        });
      }

      maturityValue = runningTotal;
    }

    if (investmentType === 'lumpsum' || investmentType === 'hybrid') {
      const lumpSumMaturity = lumpSum * Math.pow(1 + netReturnRate / 100, period);
      
      if (investmentType === 'lumpsum') {
        maturityValue = lumpSumMaturity;
        totalInvestment = lumpSum;
        
        // Create yearly breakdown for lump sum
        for (let year = 1; year <= period; year++) {
          const yearEndValue = lumpSum * Math.pow(1 + netReturnRate / 100, year);
          yearlyBreakdown.push({
            year,
            startValue: year === 1 ? lumpSum : yearlyBreakdown[year - 2].endValue,
            investment: year === 1 ? lumpSum : 0,
            endValue: yearEndValue,
            sipAmount: 0,
          });
        }

        // Cash flows for XIRR
        cashFlows = [-lumpSum, maturityValue];
        dates = [startDate, new Date(startDate.getFullYear() + period, startDate.getMonth(), startDate.getDate())];
      } else {
        maturityValue += lumpSumMaturity;
        totalInvestment += lumpSum;
        
        // Add lump sum to first year breakdown
        if (yearlyBreakdown.length > 0) {
          yearlyBreakdown[0].investment += lumpSum;
          yearlyBreakdown[0].startValue += lumpSum;
          for (let i = 0; i < yearlyBreakdown.length; i++) {
            yearlyBreakdown[i].endValue += lumpSum * Math.pow(1 + netReturnRate / 100, i + 1);
          }
        }
      }
    }

    // Add final maturity value to cash flows
    if (investmentType === 'sip' || investmentType === 'hybrid') {
      cashFlows.push(maturityValue);
      dates.push(new Date(startDate.getFullYear() + period, startDate.getMonth(), startDate.getDate()));
    }

    // Calculate XIRR
    const xirr = cashFlows.length > 1 ? calculateXIRR(cashFlows, dates) : netReturnRate;

    // Calculate absolute returns
    const absoluteReturns = maturityValue - totalInvestment;
    const returnsPercentage = (absoluteReturns / totalInvestment) * 100;

    // Calculate tax implications
    const ltcg = Math.max(0, absoluteReturns - 100000); // LTCG above 1 lakh for equity
    const ltcgTax = fundCategory.includes('debt') ? absoluteReturns * 0.2 : ltcg * 0.1;
    const postTaxReturns = absoluteReturns - ltcgTax;

    // Goal planning
    const monthsToGoal = goal > 0 ? Math.log(goal / (sip * 12)) / Math.log(1 + netReturnRate / 100) : 0;
    const requiredSIP = goal > 0 ? goal / (((Math.pow(1 + monthlyReturn, period * 12) - 1) / monthlyReturn) * (1 + monthlyReturn)) : 0;

    // Real value (inflation adjusted)
    const realMaturityValue = maturityValue / Math.pow(1 + inflation / 100, period);

    return {
      totalInvestment,
      maturityValue,
      absoluteReturns,
      returnsPercentage,
      xirr: Math.max(0, xirr),
      realReturn,
      realMaturityValue,
      yearlyBreakdown,
      ltcgTax,
      postTaxReturns,
      monthsToGoal: Math.max(0, monthsToGoal),
      requiredSIP: Math.max(0, requiredSIP),
      netReturnRate,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateMutualFundReturns();
    setResults(calculatedResults);
  }, [
    investmentType, sipAmount, lumpSumAmount, expectedReturn, investmentPeriod, 
    stepUpPercentage, expenseRatio, inflationRate, goalAmount, taxRate, fundCategory
  ]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>Mutual Fund Returns Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate returns for SIP, Lump Sum investments with comprehensive analysis and goal planning
      </Text>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.seoTitle}>Complete Guide to Mutual Fund Returns Calculator</Text>
      <Text style={styles.seoSubtitle}>Master Mutual Fund Investing with Advanced Analytics</Text>
      
      <Text style={styles.seoText}>
        Our comprehensive Mutual Fund Returns Calculator is designed to help investors make informed decisions 
        about their mutual fund investments. Whether you're planning SIP investments, lump sum investments, 
        or a combination of both, this calculator provides detailed analysis including XIRR calculations, 
        tax implications, and inflation-adjusted returns.
      </Text>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🎯 What is Mutual Fund Investment?</Text>
        <Text style={styles.seoText}>
          Mutual funds are investment vehicles that pool money from multiple investors to purchase a diversified 
          portfolio of stocks, bonds, or other securities. Professional fund managers make investment decisions 
          based on the fund's objectives, providing individual investors access to professionally managed, 
          diversified portfolios that would be difficult to create independently.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📊 SIP vs Lump Sum vs Hybrid Investment</Text>
        <View style={styles.comparisonContainer}>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>SIP (Systematic Investment Plan)</Text>
            <Text style={styles.comparisonText}>• Regular monthly investments</Text>
            <Text style={styles.comparisonText}>• Rupee cost averaging benefits</Text>
            <Text style={styles.comparisonText}>• Disciplined investment approach</Text>
            <Text style={styles.comparisonText}>• Lower market timing risk</Text>
          </View>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>Lump Sum Investment</Text>
            <Text style={styles.comparisonText}>• One-time large investment</Text>
            <Text style={styles.comparisonText}>• Potential for higher returns in bull markets</Text>
            <Text style={styles.comparisonText}>• Requires market timing skills</Text>
            <Text style={styles.comparisonText}>• Higher volatility exposure</Text>
          </View>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>Hybrid Approach</Text>
            <Text style={styles.comparisonText}>• Combines SIP and lump sum</Text>
            <Text style={styles.comparisonText}>• Balanced risk approach</Text>
            <Text style={styles.comparisonText}>• Optimal for varying cash flows</Text>
            <Text style={styles.comparisonText}>• Flexible investment strategy</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🔧 Key Features of Our Calculator</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Multiple investment types (SIP, Lump Sum, Hybrid)</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Fund category-wise expected returns</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Step-up SIP calculations</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Expense ratio and exit load considerations</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• XIRR and absolute return calculations</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Tax implications (LTCG/STCG)</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Inflation-adjusted real returns</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Goal-based investment planning</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Year-wise investment breakdown</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Comparative analysis with other investments</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📈 Fund Categories and Expected Returns</Text>
        <View style={styles.taxRateTable}>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateHeader}>Fund Category</Text>
            <Text style={styles.taxRateHeader}>Risk Level</Text>
            <Text style={styles.taxRateHeader}>Expected Return</Text>
            <Text style={styles.taxRateHeader}>Investment Horizon</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Large Cap Equity</Text>
            <Text style={styles.taxRateCell}>Moderate</Text>
            <Text style={styles.taxRateCell}>12%</Text>
            <Text style={styles.taxRateCell}>5+ years</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Mid Cap Equity</Text>
            <Text style={styles.taxRateCell}>High</Text>
            <Text style={styles.taxRateCell}>15%</Text>
            <Text style={styles.taxRateCell}>7+ years</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Small Cap Equity</Text>
            <Text style={styles.taxRateCell}>Very High</Text>
            <Text style={styles.taxRateCell}>18%</Text>
            <Text style={styles.taxRateCell}>10+ years</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Hybrid Funds</Text>
            <Text style={styles.taxRateCell}>Moderate</Text>
            <Text style={styles.taxRateCell}>10%</Text>
            <Text style={styles.taxRateCell}>3+ years</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Debt Funds</Text>
            <Text style={styles.taxRateCell}>Low</Text>
            <Text style={styles.taxRateCell}>7%</Text>
            <Text style={styles.taxRateCell}>3+ years</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📝 How to Use the Calculator</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>1</Text>
            </View>
            <Text style={styles.stepText}>Select your investment type (SIP, Lump Sum, or Hybrid)</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>2</Text>
            </View>
            <Text style={styles.stepText}>Choose the appropriate fund category based on your risk appetite</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>3</Text>
            </View>
            <Text style={styles.stepText}>Enter your investment amount and time horizon</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>4</Text>
            </View>
            <Text style={styles.stepText}>Adjust expected returns, expense ratio, and other parameters</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>5</Text>
            </View>
            <Text style={styles.stepText}>Review comprehensive results including tax implications</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>6</Text>
            </View>
            <Text style={styles.stepText}>Analyze year-wise breakdown and goal planning recommendations</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>💡 Investment Optimization Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Step-Up SIP Strategy</Text>
              <Text style={styles.strategyText}>
                Increase your SIP amount by 10-15% annually to counter inflation and accelerate wealth creation. 
                This strategy can significantly boost your final corpus over long investment horizons.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Asset Allocation Approach</Text>
              <Text style={styles.strategyText}>
                Diversify across different fund categories based on your age and risk tolerance. 
                Young investors can allocate more to equity funds, while older investors should focus on hybrid and debt funds.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Tax Optimization</Text>
              <Text style={styles.strategyText}>
                Hold equity funds for more than 1 year to benefit from LTCG tax rates. 
                Consider ELSS funds for tax deductions under Section 80C while building wealth.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Regular Review and Rebalancing</Text>
              <Text style={styles.strategyText}>
                Review your portfolio annually and rebalance if needed. 
                Switch to lower-cost funds and adjust allocation based on changing life goals and market conditions.
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>⚖️ Legal and Regulatory Considerations</Text>
        <Text style={styles.seoText}>
          Mutual fund investments are subject to market risks and regulatory changes. All mutual funds are regulated 
          by SEBI (Securities and Exchange Board of India). Investors should read scheme documents carefully before 
          investing. Past performance doesn't guarantee future returns. Tax implications may vary based on individual 
          circumstances and prevailing tax laws.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>❌ Common Investment Mistakes to Avoid</Text>
        <Text style={styles.seoText}>• Investing without clear financial goals</Text>
        <Text style={styles.seoText}>• Choosing funds based solely on past performance</Text>
        <Text style={styles.seoText}>• Ignoring expense ratios and exit loads</Text>
        <Text style={styles.seoText}>• Frequent switching between funds</Text>
        <Text style={styles.seoText}>• Not considering tax implications</Text>
        <Text style={styles.seoText}>• Panic selling during market downturns</Text>
        <Text style={styles.seoText}>• Over-diversification leading to average returns</Text>
        <Text style={styles.seoText}>• Not reviewing and rebalancing portfolios</Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🎯 Success Stories and Examples</Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Case Study 1:</Text> A 25-year-old professional investing ₹10,000 monthly 
          in large-cap equity funds for 30 years at 12% returns could accumulate approximately ₹3.5 crores, 
          with total investment of ₹36 lakhs.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Case Study 2:</Text> Step-up SIP of ₹5,000 starting amount with 
          10% annual increase for 20 years in mid-cap funds at 15% returns could result in ₹2.8 crores corpus 
          with total investment of ₹33 lakhs.
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>Investment Details</Text>
      
      {/* Investment Type Selection */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Investment Type</Text>
        <View style={styles.segmentedControl}>
          {investmentTypes.map((type) => (
            <TouchableOpacity
              key={type.value}
              style={[
                styles.segmentButton,
                investmentType === type.value && styles.segmentButtonActive,
              ]}
              onPress={() => setInvestmentType(type.value)}
            >
              <Text style={styles.segmentIcon}>{type.icon}</Text>
              <Text style={[
                styles.segmentButtonText,
                investmentType === type.value && styles.segmentButtonTextActive,
              ]}>
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Fund Category Selection */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Fund Category</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
          <View style={styles.categoryContainer}>
            {fundCategories.map((category) => (
              <TouchableOpacity
                key={category.value}
                style={[
                  styles.categoryCard,
                  fundCategory === category.value && styles.categoryCardActive,
                ]}
                onPress={() => {
                  setFundCategory(category.value);
                  setExpectedReturn(category.expectedReturn);
                  setExpenseRatio(category.expenseRatio);
                }}
              >
                <Text style={[
                  styles.categoryTitle,
                  fundCategory === category.value && styles.categoryTitleActive,
                ]}>
                  {category.label}
                </Text>
                <Text style={[
                  styles.categoryRisk,
                  fundCategory === category.value && styles.categoryRiskActive,
                ]}>
                  Risk: {category.risk}
                </Text>
                <Text style={[
                  styles.categoryReturn,
                  fundCategory === category.value && styles.categoryReturnActive,
                ]}>
                  ~{category.expectedReturn}% returns
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* SIP Amount (if applicable) */}
      {(investmentType === 'sip' || investmentType === 'hybrid') && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Monthly SIP Amount</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.textInput}
              value={sipAmount}
              onChangeText={setSipAmount}
              placeholder="10000"
              keyboardType="numeric"
            />
          </View>
        </View>
      )}

      {/* Lump Sum Amount (if applicable) */}
      {(investmentType === 'lumpsum' || investmentType === 'hybrid') && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Lump Sum Amount</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.textInput}
              value={lumpSumAmount}
              onChangeText={setLumpSumAmount}
              placeholder="500000"
              keyboardType="numeric"
            />
          </View>
        </View>
      )}

      {/* Expected Return */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Expected Annual Return: {expectedReturn}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={5}
          maximumValue={25}
          value={expectedReturn}
          onValueChange={setExpectedReturn}
          step={0.5}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>5%</Text>
          <Text style={styles.sliderLabel}>25%</Text>
        </View>
      </View>

      {/* Investment Period */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Investment Period: {investmentPeriod} years</Text>
        <Slider
          style={styles.slider}
          minimumValue={1}
          maximumValue={30}
          value={investmentPeriod}
          onValueChange={setInvestmentPeriod}
          step={1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>1 year</Text>
          <Text style={styles.sliderLabel}>30 years</Text>
        </View>
      </View>

      {/* Step-up Percentage (for SIP) */}
      {(investmentType === 'sip' || investmentType === 'hybrid') && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Annual Step-up: {stepUpPercentage}%</Text>
          <Slider
            style={styles.slider}
            minimumValue={0}
            maximumValue={20}
            value={stepUpPercentage}
            onValueChange={setStepUpPercentage}
            step={1}
            minimumTrackTintColor={colors.primary}
            maximumTrackTintColor={colors.border}
            thumbStyle={styles.sliderThumb}
          />
          <View style={styles.sliderLabels}>
            <Text style={styles.sliderLabel}>0%</Text>
            <Text style={styles.sliderLabel}>20%</Text>
          </View>
        </View>
      )}

      {/* Expense Ratio */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Expense Ratio: {expenseRatio}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={0.1}
          maximumValue={3}
          value={expenseRatio}
          onValueChange={setExpenseRatio}
          step={0.1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>0.1%</Text>
          <Text style={styles.sliderLabel}>3%</Text>
        </View>
      </View>

      {/* Inflation Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Inflation Rate: {inflationRate}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={3}
          maximumValue={10}
          value={inflationRate}
          onValueChange={setInflationRate}
          step={0.5}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>3%</Text>
          <Text style={styles.sliderLabel}>10%</Text>
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { totalInvestment, absoluteReturns, maturityValue } = results;
    const investmentPercentage = (totalInvestment / maturityValue) * 100;
    const returnsPercentage = (absoluteReturns / maturityValue) * 100;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const investmentStrokeDasharray = `${(investmentPercentage / 100) * circumference} ${circumference}`;
    const returnsStrokeDasharray = `${(returnsPercentage / 100) * circumference} ${circumference}`;
    const returnsStrokeDashoffset = -((investmentPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Investment Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* Investment amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={investmentStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Returns */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={returnsStrokeDasharray}
              strokeDashoffset={returnsStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Maturity Value
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(maturityValue)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Investment ({investmentPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Returns ({returnsPercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const { 
      totalInvestment, 
      maturityValue, 
      absoluteReturns, 
      returnsPercentage,
      xirr,
      realMaturityValue,
      ltcgTax,
      postTaxReturns,
      netReturnRate
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>Investment Results</Text>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Investment</Text>
          <Text style={styles.resultValue}>{formatCurrency(totalInvestment)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Maturity Value</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(maturityValue)}
          </Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Absolute Returns</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(absoluteReturns)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Returns Percentage</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {returnsPercentage.toFixed(2)}%
          </Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>XIRR (Annualized)</Text>
          <Text style={styles.resultValue}>{xirr.toFixed(2)}%</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Net Return (After Expense)</Text>
          <Text style={styles.resultValue}>{netReturnRate.toFixed(2)}%</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Real Value (Inflation Adj.)</Text>
          <Text style={styles.resultValue}>{formatCurrency(realMaturityValue)}</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>LTCG Tax</Text>
          <Text style={[styles.resultValue, { color: colors.error }]}>
            {formatCurrency(ltcgTax)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Post-Tax Returns</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(postTaxReturns)}
          </Text>
        </View>
      </View>
    );
  };

  const renderGoalPlanning = () => {
    if (!results) return null;

    const { requiredSIP, monthsToGoal } = results;
    const goal = parseFloat(goalAmount) || 0;

    return (
      <View style={styles.goalCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowGoalPlanning(!showGoalPlanning)}
        >
          <Text style={styles.cardTitle}>Goal-Based Planning</Text>
          <Ionicons
            name={showGoalPlanning ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showGoalPlanning && (
          <View style={styles.cardContent}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Target Goal Amount</Text>
              <View style={styles.inputContainer}>
                <Text style={styles.currencySymbol}>₹</Text>
                <TextInput
                  style={styles.textInput}
                  value={goalAmount}
                  onChangeText={setGoalAmount}
                  placeholder="2000000"
                  keyboardType="numeric"
                />
              </View>
            </View>

            {goal > 0 && (
              <View style={styles.goalResults}>
                <View style={styles.goalMetric}>
                  <Text style={styles.metricLabel}>Required Monthly SIP</Text>
                  <Text style={styles.metricValue}>{formatCurrency(requiredSIP)}</Text>
                </View>
                
                <View style={styles.goalMetric}>
                  <Text style={styles.metricLabel}>Time to Reach Goal</Text>
                  <Text style={styles.metricValue}>
                    {monthsToGoal > 0 ? `${(monthsToGoal / 12).toFixed(1)} years` : 'Goal already achieved'}
                  </Text>
                </View>

                <View style={styles.recommendationBox}>
                  <Text style={styles.recommendationTitle}>💡 Recommendation</Text>
                  <Text style={styles.recommendationText}>
                    {requiredSIP > parseFloat(sipAmount) 
                      ? `Increase your SIP by ${formatCurrency(requiredSIP - parseFloat(sipAmount))} to reach your goal.`
                      : 'Your current SIP is sufficient to achieve your goal!'
                    }
                  </Text>
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderYearlyBreakdown = () => {
    if (!results || !results.yearlyBreakdown) return null;

    const { yearlyBreakdown } = results;

    return (
      <View style={styles.breakdownCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowYearlyBreakdown(!showYearlyBreakdown)}
        >
          <Text style={styles.cardTitle}>Year-wise Investment Growth</Text>
          <Ionicons
            name={showYearlyBreakdown ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showYearlyBreakdown && (
          <View style={styles.cardContent}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, { flex: 1 }]}>Year</Text>
              <Text style={[styles.tableHeaderText, { flex: 2 }]}>Investment</Text>
              <Text style={[styles.tableHeaderText, { flex: 2 }]}>Value</Text>
              <Text style={[styles.tableHeaderText, { flex: 2 }]}>Returns</Text>
            </View>
            
            <ScrollView style={styles.tableContainer} nestedScrollEnabled>
              {yearlyBreakdown.slice(0, displayedRows).map((row, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 1 }]}>{row.year}</Text>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(row.investment)}</Text>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(row.endValue)}</Text>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(row.endValue - row.investment)}</Text>
                </View>
              ))}
            </ScrollView>
            
            {yearlyBreakdown.length > displayedRows && (
              <TouchableOpacity
                style={styles.showMoreButton}
                onPress={() => setDisplayedRows(displayedRows + 5)}
              >
                <Text style={styles.showMoreText}>Show More Years</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderComparison = () => {
    if (!results) return null;

    const { maturityValue, totalInvestment, xirr } = results;
    
    // Compare with other investment options
    const fdReturns = totalInvestment * Math.pow(1.065, investmentPeriod);
    const ppfReturns = totalInvestment * Math.pow(1.075, investmentPeriod);
    const goldReturns = totalInvestment * Math.pow(1.08, investmentPeriod);

    const comparisons = [
      { name: 'Mutual Fund', value: maturityValue, returns: xirr, color: colors.primary },
      { name: 'Fixed Deposit', value: fdReturns, returns: 6.5, color: colors.warning },
      { name: 'PPF', value: ppfReturns, returns: 7.5, color: colors.success },
      { name: 'Gold', value: goldReturns, returns: 8, color: '#FFD700' },
    ];

    return (
      <View style={styles.comparisonCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowComparison(!showComparison)}
        >
          <Text style={styles.cardTitle}>Investment Comparison</Text>
          <Ionicons
            name={showComparison ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showComparison && (
          <View style={styles.cardContent}>
            {comparisons.map((item, index) => (
              <View key={index} style={styles.comparisonItem}>
                <View style={styles.comparisonHeader}>
                  <View style={[styles.comparisonIndicator, { backgroundColor: item.color }]} />
                  <Text style={styles.comparisonName}>{item.name}</Text>
                  <Text style={styles.comparisonReturns}>{item.returns.toFixed(1)}%</Text>
                </View>
                <Text style={styles.comparisonValue}>{formatCurrency(item.value)}</Text>
                <View style={styles.comparisonBar}>
                  <View 
                    style={[
                      styles.comparisonBarFill, 
                      { 
                        width: `${(item.value / Math.max(...comparisons.map(c => c.value))) * 100}%`,
                        backgroundColor: item.color 
                      }
                    ]} 
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>About Mutual Fund Investing</Text>
      <Text style={styles.infoSubtitle}>Understanding Mutual Fund Returns</Text>
      
      <Text style={styles.infoText}>
        Mutual funds pool money from multiple investors to invest in a diversified portfolio of 
        stocks, bonds, or other securities. They offer professional management, diversification, 
        and the potential for higher returns compared to traditional savings instruments.
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="trending-up" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>SIP Advantage:</Text> Systematic Investment Plans 
          help average out market volatility through rupee cost averaging, making it ideal for 
          long-term wealth creation.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="shield-checkmark" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Diversification:</Text> Mutual funds spread risk 
          across multiple securities, reducing the impact of poor performance by any single investment.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="calculator" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Expense Ratio:</Text> This annual fee covers fund 
          management costs. Lower expense ratios mean more of your money stays invested and compounds over time.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is the difference between SIP and Lump Sum investment?",
        answer: "SIP (Systematic Investment Plan) involves investing a fixed amount regularly, which helps average out market volatility. Lump sum investment involves investing a large amount at once, which can be beneficial during market lows but carries timing risk."
      },
      {
        question: "How is XIRR different from absolute returns?",
        answer: "XIRR (Extended Internal Rate of Return) considers the timing of cash flows and provides an annualized return rate. Absolute returns show the total percentage gain without considering the time factor. XIRR is more accurate for comparing investments with different time periods."
      },
      {
        question: "What is expense ratio and how does it affect returns?",
        answer: "Expense ratio is the annual fee charged by mutual funds for managing your investment. It's expressed as a percentage of your investment. A lower expense ratio means more of your money remains invested, leading to higher long-term returns through compounding."
      },
      {
        question: "How are mutual fund returns taxed?",
        answer: "For equity funds held over 1 year, LTCG tax of 10% applies on gains above ₹1 lakh per year. For debt funds, gains are taxed at 20% with indexation benefit if held over 3 years. Short-term gains are taxed as per your income tax slab."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>What is step-up SIP and why should I use it?</Text>
              <Text style={styles.faqAnswer}>
                Step-up SIP allows you to increase your SIP amount annually by a fixed percentage. 
                This helps counter inflation and accelerates wealth creation as your income grows. 
                Even a 10% annual increase can significantly boost your final corpus.
              </Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      padding: 20,
      gap: 20,
      width: '100%',
    },
    leftPanel: {
      flex: isMobile ? 1 : 1.2,
    },
    rightPanel: {
      flex: 1,
      gap: 20,
    },
    calculatorCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    textInput: {
      flex: 1,
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    segmentedControl: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 4,
    },
    segmentButton: {
      flex: 1,
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderRadius: 6,
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 4,
    },
    segmentButtonActive: {
      backgroundColor: colors.primary,
    },
    segmentIcon: {
      fontSize: 16,
    },
    segmentButtonText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    segmentButtonTextActive: {
      color: colors.background,
    },
    categoryScroll: {
      marginTop: 8,
    },
    categoryContainer: {
      flexDirection: 'row',
      gap: 12,
      paddingRight: 20,
    },
    categoryCard: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      minWidth: 140,
      borderWidth: 1,
      borderColor: colors.border,
    },
    categoryCardActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    categoryTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    categoryTitleActive: {
      color: colors.primary,
    },
    categoryRisk: {
      fontSize: 10,
      color: colors.textSecondary,
      marginBottom: 2,
    },
    categoryRiskActive: {
      color: colors.primary,
    },
    categoryReturn: {
      fontSize: 10,
      color: colors.success,
      fontWeight: '600',
    },
    categoryReturnActive: {
      color: colors.primary,
    },
    slider: {
      height: 40,
      marginVertical: 10,
    },
    sliderThumb: {
      backgroundColor: colors.primary,
      width: 20,
      height: 20,
    },
    sliderLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    sliderLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    chartCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartLegend: {
      marginTop: 20,
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultsCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    goalCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    cardContent: {
      marginTop: 20,
    },
    goalResults: {
      marginTop: 20,
    },
    goalMetric: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    metricLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    metricValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    recommendationBox: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 12,
    },
    recommendationTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    recommendationText: {
      fontSize: 13,
      color: colors.textSecondary,
      lineHeight: 18,
    },
    breakdownCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      borderRadius: 8,
      padding: 12,
      marginBottom: 8,
    },
    tableHeaderText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.background,
      textAlign: 'center',
    },
    tableContainer: {
      maxHeight: 300,
    },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tableCell: {
      fontSize: 13,
      color: colors.text,
      textAlign: 'center',
    },
    showMoreButton: {
      backgroundColor: colors.primary,
      borderRadius: 8,
      padding: 12,
      alignItems: 'center',
      marginTop: 12,
    },
    showMoreText: {
      color: colors.background,
      fontSize: 14,
      fontWeight: '600',
    },
    comparisonCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    comparisonItem: {
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    comparisonHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    comparisonIndicator: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginRight: 8,
    },
    comparisonName: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      flex: 1,
    },
    comparisonReturns: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    comparisonValue: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    comparisonBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      overflow: 'hidden',
    },
    comparisonBarFill: {
      height: '100%',
      borderRadius: 2,
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 16,
    },
    tipBox: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 8,
    },
    tipText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
    // SEO Content Styles
    seoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    seoSubtitle: {
      fontSize: 18,
      color: colors.textSecondary,
      marginBottom: 20,
      textAlign: 'center',
    },
    seoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
      marginBottom: 16,
    },
    seoSection: {
      marginBottom: 24,
    },
    seoSectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    comparisonContainer: {
      gap: 16,
    },
    comparisonTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.primary,
      marginBottom: 8,
    },
    comparisonText: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    featuresList: {
      gap: 8,
    },
    featureItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    featureText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    taxRateTable: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      overflow: 'hidden',
    },
    taxRateRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    taxRateHeader: {
      flex: 1,
      backgroundColor: colors.primary,
      color: colors.background,
      fontSize: 12,
      fontWeight: 'bold',
      padding: 12,
      textAlign: 'center',
    },
    taxRateCell: {
      flex: 1,
      backgroundColor: colors.background,
      fontSize: 12,
      color: colors.text,
      padding: 12,
      textAlign: 'center',
      borderRightWidth: 1,
      borderRightColor: colors.border,
    },
    stepsList: {
      gap: 16,
    },
    stepItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    stepNumber: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
    },
    stepNumberText: {
      color: colors.background,
      fontSize: 14,
      fontWeight: 'bold',
    },
    stepText: {
      flex: 1,
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    strategiesList: {
      gap: 16,
    },
    strategyItem: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    strategyContent: {
      gap: 8,
    },
    strategyTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
    },
    strategyText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderGoalPlanning()}
      {renderYearlyBreakdown()}
      {renderComparison()}
      {renderInformation()}
      {renderSEOContent()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default MutualFundReturnsCalculatorScreen;